import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getListingReportDetail, handleListingReport, IMAGE_BASE_URL } from '../../services/api';
import {
  FiArrowLeft,
  FiAlertTriangle,
  FiCheckCircle,
  FiXCircle,
  FiShield,
  FiTrash2,
  FiEye,
  FiUser,
  FiCalendar,
  FiClock,
  FiPackage,
  FiMessageSquare,
  FiMail,
  FiPhone,
  FiFlag,
  FiRefreshCw,
  FiAlertCircle,
  FiInfo,
  FiChevronRight,
  FiX,
} from 'react-icons/fi';
import {Store, History} from 'lucide-react';

function ReportDetail({ admin }) {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const actionOptions = [
    { 
      value: 'dismiss', 
      label: 'Dismiss Report', 
      icon: <FiCheckCircle className="w-4 h-4" />, 
      color: 'text-green-400',
      bg: 'bg-green-900/30',
      description: 'Mark this report as false or invalid'
    },
    { 
      value: 'warn_seller', 
      label: 'Warn Seller', 
      icon: <FiAlertTriangle className="w-4 h-4" />, 
      color: 'text-yellow-400',
      bg: 'bg-yellow-900/30',
      description: 'Send warning to seller about the listing'
    },
    { 
      value: 'suspend_listing', 
      label: 'Suspend Listing', 
      icon: <FiShield className="w-4 h-4" />, 
      color: 'text-orange-400',
      bg: 'bg-orange-900/30',
      description: 'Temporarily suspend this listing'
    },
    { 
      value: 'delete_listing', 
      label: 'Delete Listing', 
      icon: <FiTrash2 className="w-4 h-4" />, 
      color: 'text-red-400',
      bg: 'bg-red-900/30',
      description: 'Permanently remove this listing'
    }
  ];

  const statusConfig = {
    pending: { 
      color: 'text-yellow-400', 
      bg: 'bg-yellow-900/30', 
      border: 'border-yellow-800/50', 
      icon: <FiClock className="w-4 h-4" />, 
      label: 'Pending Review' 
    },
    reviewed: { 
      color: 'text-blue-400', 
      bg: 'bg-blue-900/30', 
      border: 'border-blue-800/50', 
      icon: <FiCheckCircle className="w-4 h-4" />, 
      label: 'Reviewed' 
    },
    dismissed: { 
      color: 'text-gray-400', 
      bg: 'bg-gray-700', 
      border: 'border-gray-600', 
      icon: <FiXCircle className="w-4 h-4" />, 
      label: 'Dismissed' 
    }
  };

  const severityConfig = {
    high: { 
      color: 'text-red-400', 
      bg: 'bg-red-900/30', 
      border: 'border-red-800/50', 
      icon: <FiAlertTriangle className="w-4 h-4" />, 
      label: 'High' 
    },
    medium: { 
      color: 'text-yellow-400', 
      bg: 'bg-yellow-900/30', 
      border: 'border-yellow-800/50', 
      icon: <FiAlertTriangle className="w-4 h-4" />, 
      label: 'Medium' 
    },
    low: { 
      color: 'text-green-400', 
      bg: 'bg-green-900/30', 
      border: 'border-green-800/50', 
      icon: <FiAlertTriangle className="w-4 h-4" />, 
      label: 'Low' 
    }
  };

  const fetchReportDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getListingReportDetail(reportId);
      
      if (response.success && response.data) {
        setReport(response.data);
      } else {
        setReport(response);
      }
    } catch (error) {
      setError('Failed to load report details: ' + error.message);
      console.error('Error fetching report details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportDetail();
  }, [reportId]);

  const handleAction = async (action, adminNotes = '', severity = 'medium', sendEmail = true) => {
    try {
      setActionLoading(true);
      setError('');
      
      const sellerUserId = report?.seller_user_id || report?.seller_id;
      
      if (!sellerUserId) {
        setError('Cannot process action: Seller information not found.');
        return;
      }
      
      await handleListingReport(
        reportId, 
        action, 
        adminNotes, 
        severity, 
        sendEmail,
        sellerUserId
      );
      
      setSuccessMessage('Action completed successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchReportDetail();
      setShowActionModal(false);
    } catch (error) {
      setError('Failed to complete action: ' + (error.response?.data?.message || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₦0.00';
    return `₦${parseFloat(amount).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          <div className="mb-6">
            <div className="h-8 bg-gray-700 rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                  <div className="h-6 bg-gray-700 rounded w-48 mb-4"></div>
                  <div className="space-y-4">
                    {[...Array(3)].map((_, j) => (
                      <div key={j} className="h-4 bg-gray-700 rounded w-full"></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-6">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                  <div className="h-6 bg-gray-700 rounded w-32 mb-4"></div>
                  <div className="space-y-3">
                    {[...Array(2)].map((_, j) => (
                      <div key={j} className="h-4 bg-gray-700 rounded w-full"></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="text-gray-600 text-6xl mb-6">🔍</div>
          <h3 className="text-xl font-medium text-white mb-2">Report not found</h3>
          <p className="text-gray-400 mb-6">The report you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/admin/reports/listings')}
            className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium inline-flex items-center hover:scale-[1.02]"
          >
            <FiArrowLeft className="mr-2" />
            Back to Reports
          </button>
        </div>
      </div>
    );
  }

  const status = statusConfig[report.status] || statusConfig.pending;
  const severity = severityConfig[report.severity] || severityConfig.medium;

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-6">
      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <Link
              to="/admin/reports/listings"
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-gray-600 transition-colors"
            >
              <FiArrowLeft className="w-5 h-5 text-gray-400" />
            </Link>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                <span className="text-[#ff660e]">Report</span> Details
              </h1>
              <p className="text-gray-400 mt-1">Report #{report.report_id} • {report.listing_title}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${status.bg} ${status.color} ${status.border}`}>
              {status.icon}
              <span className="ml-2">{status.label}</span>
            </span>
            
            {report.severity && (
              <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${severity.bg} ${severity.color} ${severity.border}`}>
                {severity.icon}
                <span className="ml-2">{severity.label}</span>
              </span>
            )}
            
            <button
              onClick={() => fetchReportDetail()}
              disabled={loading}
              className="p-2 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-blue-500 transition-all duration-200 disabled:opacity-50"
            >
              <FiRefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Report Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Report Information */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-6">
              <div className="w-10 h-10 rounded-lg bg-red-900/30 flex items-center justify-center">
                <FiFlag className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h2 className="font-bold text-white">Report Information</h2>
                <p className="text-sm text-gray-400">Complete report details and evidence</p>
              </div>
            </div>
            
            <div className="space-y-6">
              {/* Report Summary */}
              <div className="bg-gray-700/30 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500">Report ID</p>
                    <p className="font-bold text-white text-lg">#{report.report_id}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500">Reported Date</p>
                    <p className="font-medium text-gray-300">{formatDate(report.created_at)}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500">Reason</p>
                    <p className="font-medium text-gray-300">{report.reason}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500">Days Pending</p>
                    <p className="font-medium text-yellow-400">{report.days_pending || 0} days</p>
                  </div>
                </div>
                
                {report.description && (
                  <div className="mt-4 pt-4 border-t border-gray-600">
                    <div className="flex items-center space-x-2 mb-2">
                      <FiMessageSquare className="w-4 h-4 text-gray-400" />
                      <p className="text-sm font-medium text-gray-300">Description</p>
                    </div>
                    <p className="text-gray-300 whitespace-pre-wrap bg-gray-800/50 rounded-lg p-3">
                      {report.description}
                    </p>
                  </div>
                )}
              </div>

              {/* Reporter Information */}
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-900/30 flex items-center justify-center">
                    <FiUser className="w-4 h-4 text-blue-400" />
                  </div>
                  <h3 className="font-medium text-white">Reporter Information</h3>
                </div>
                <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg p-4">
                  <div className="flex items-center space-x-4">
                    {report.reporter?.profile_picture ? (
                      <img
                        src={report.reporter.profile_picture}
                        alt={report.reporter.first_name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-blue-800/50"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-blue-900/30 rounded-full flex items-center justify-center border-2 border-blue-800/50">
                        <FiUser className="w-8 h-8 text-blue-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-white">
                            {report.reporter_first_name} {report.reporter_last_name}
                          </p>
                          <p className="text-sm text-blue-300">{report.reporter_email}</p>
                        </div>
                        <button
                          onClick={() => navigate(`/admin/users/${report.reported_by}`)}
                          className="px-3 py-1.5 bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 rounded-lg text-sm font-medium transition-colors flex items-center"
                        >
                          <FiEye className="mr-1" />
                          View Profile
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div className="text-xs text-gray-400">
                          Member since {report.reporter?.joined_date ? formatDate(report.reporter.joined_date) : 'N/A'}
                        </div>
                        <div className="text-xs text-gray-400 text-right">
                          Report Count: {report.reporter?.report_count || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Listing Information */}
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-green-900/30 flex items-center justify-center">
                    <FiPackage className="w-4 h-4 text-green-400" />
                  </div>
                  <h3 className="font-medium text-white">Listing Information</h3>
                </div>
                <div className="bg-green-900/20 border border-green-800/30 rounded-lg p-4">
                  <div className="flex flex-col md:flex-row gap-4">
                    {report.listing_images && report.listing_images.length > 0 && (
                      <div className="relative">
                        <img
                          src={`${IMAGE_BASE_URL}${report.listing_images[0].image_url}`}
                          alt={report.listing_title}
                          className="w-32 h-32 object-cover rounded-lg border border-gray-600"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/128x128/374151/6b7280?text=No+Image';
                          }}
                        />
                        <div className="absolute -bottom-2 -right-2 bg-[#ff660e] text-white text-xs font-bold px-2 py-1 rounded-lg">
                          #{report.listing_id}
                        </div>
                      </div>
                    )}
                    <div className="flex-1">
                      <h4 className="font-bold text-white text-lg mb-2">{report.listing_title}</h4>
                      {report.listing_description && (
                        <p className="text-gray-400 mb-3 line-clamp-2">{report.listing_description}</p>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-800/50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Price</p>
                          <p className="font-bold text-white text-lg">{formatCurrency(report.price)}</p>
                        </div>
                        <div className="bg-gray-800/50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Status</p>
                          <p className={`font-medium ${
                            report.listing_status === 'active' ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {report.listing_status?.charAt(0).toUpperCase() + report.listing_status?.slice(1)}
                          </p>
                        </div>
                        <div className="bg-gray-800/50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Views</p>
                          <p className="font-medium text-white">{report.view_count || 0}</p>
                        </div>
                        <div className="bg-gray-800/50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Reports</p>
                          <p className="font-medium text-red-400">{report.report_count || 0}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Similar Reports */}
          {report.similar_reports && report.similar_reports.length > 0 && (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-10 h-10 rounded-lg bg-purple-900/30 flex items-center justify-center">
                  <History className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h2 className="font-bold text-white">Similar Reports</h2>
                  <p className="text-sm text-gray-400">Other reports for this listing</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {report.similar_reports.map((similarReport) => (
                  <div key={similarReport.report_id} className="bg-gray-700/30 border border-gray-600 rounded-lg p-4 hover:bg-gray-700/50 transition-colors">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-white">Report #{similarReport.report_id}</p>
                        <p className="text-sm text-gray-400">{similarReport.reason}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          By {similarReport.reporter_first_name} {similarReport.reporter_last_name}
                        </p>
                      </div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        similarReport.status === 'pending' ? 'bg-yellow-900/30 text-yellow-400' :
                        similarReport.status === 'reviewed' ? 'bg-blue-900/30 text-blue-400' :
                        'bg-gray-700 text-gray-400'
                      }`}>
                        {similarReport.status.charAt(0).toUpperCase() + similarReport.status.slice(1)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {formatDate(similarReport.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Actions & Seller Info */}
        <div className="space-y-6">
          {/* Seller Information */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-6">
              <div className="w-10 h-10 rounded-lg bg-orange-900/30 flex items-center justify-center">
                <Store className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <h2 className="font-bold text-white">Seller Information</h2>
                <p className="text-sm text-gray-400">Listing owner details</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                {report.seller?.profile_picture ? (
                  <img
                    src={report.seller.profile_picture}
                    alt={report.seller.first_name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-orange-800/50"
                  />
                ) : (
                  <div className="w-16 h-16 bg-orange-900/30 rounded-full flex items-center justify-center border-2 border-orange-800/50">
                    <Store className="w-8 h-8 text-orange-400" />
                  </div>
                )}
                <div>
                  <p className="font-bold text-white">{report.store_name}</p>
                  <p className="text-sm text-gray-400">
                    {report.seller_first_name} {report.seller_last_name}
                  </p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-2 bg-gray-700/30 rounded-lg">
                  <FiMail className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-300">{report.seller_email}</span>
                </div>
                {report.seller_phone && (
                  <div className="flex items-center space-x-3 p-2 bg-gray-700/30 rounded-lg">
                    <FiPhone className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-300">{report.seller_phone}</span>
                  </div>
                )}
                <div className="flex items-center space-x-3 p-2 bg-gray-700/30 rounded-lg">
                  <FiCalendar className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-300">
                    Seller since {formatDate(report.seller_since)}
                  </span>
                </div>
                <div className="flex items-center space-x-3 p-2 bg-gray-700/30 rounded-lg">
                  <FiPackage className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-300">
                    {report.seller_total_listings || 0} active listings
                  </span>
                </div>
              </div>
              
              <button
                onClick={() => navigate(`/admin/sellers/${report.seller_user_id}`)}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-orange-900/30 hover:bg-orange-900/50 text-orange-400 border border-orange-800/50 rounded-lg transition-all duration-200 font-medium hover:scale-[1.02]"
              >
                <FiEye className="w-4 h-4" />
                <span>View Seller Profile</span>
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          {report.status === 'pending' && (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
              <h2 className="font-bold text-white mb-6">Quick Actions</h2>
              
              <div className="space-y-3">
                {actionOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setSelectedAction(option.value);
                      setShowActionModal(true);
                    }}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border ${option.bg} ${option.color} hover:opacity-90 transition-all duration-200 hover:scale-[1.02]`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${option.bg}`}>
                        {option.icon}
                      </div>
                      <div className="text-left">
                        <span className="font-medium block">{option.label}</span>
                        <span className="text-xs text-gray-400">{option.description}</span>
                      </div>
                    </div>
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Admin Notes */}
          {report.admin_notes && (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-10 h-10 rounded-lg bg-yellow-900/30 flex items-center justify-center">
                  <FiInfo className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h2 className="font-bold text-white">Admin Notes</h2>
                  <p className="text-sm text-gray-400">Previous moderation notes</p>
                </div>
              </div>
              <div className="bg-yellow-900/20 border border-yellow-800/30 rounded-lg p-4">
                <p className="text-yellow-300 whitespace-pre-wrap">{report.admin_notes}</p>
                {report.reviewed_by_admin && (
                  <p className="text-xs text-gray-400 mt-2">
                    Reviewed by: {report.reviewed_by_admin} on {formatDate(report.reviewed_at)}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Report Metadata */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <h2 className="font-bold text-white mb-6">Report Metadata</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                <span className="text-gray-400">Report ID:</span>
                <span className="font-medium text-white">#{report.report_id}</span>
              </div>
              <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                <span className="text-gray-400">Created:</span>
                <span className="font-medium text-gray-300">{formatDate(report.created_at)}</span>
              </div>
              <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                <span className="text-gray-400">Last Updated:</span>
                <span className="font-medium text-gray-300">{formatDate(report.updated_at)}</span>
              </div>
              {report.reviewed_at && (
                <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                  <span className="text-gray-400">Reviewed:</span>
                  <span className="font-medium text-blue-400">{formatDate(report.reviewed_at)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Modal */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-md animate-fade-in">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">
                  {actionOptions.find(a => a.value === selectedAction)?.label}
                </h3>
                <button
                  onClick={() => {
                    setShowActionModal(false);
                    setSelectedAction('');
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <FiX className="text-xl" />
                </button>
              </div>
              <p className="text-sm text-gray-400 mt-2">
                You are about to {selectedAction.replace('_', ' ')} for listing:
                <strong className="text-white ml-1">{report.listing_title}</strong>
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Admin Notes *
                </label>
                <textarea
                  id="adminNotes"
                  rows="4"
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white placeholder-gray-500"
                  placeholder="Explain the reason for this action..."
                  defaultValue=""
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Severity
                </label>
                <select
                  id="severity"
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white"
                  defaultValue="medium"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-gray-700/30 rounded-lg">
                <input
                  type="checkbox"
                  id="sendEmail"
                  defaultChecked
                  className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-600 rounded bg-gray-700"
                />
                <label htmlFor="sendEmail" className="text-sm text-gray-300">
                  Send email notification to involved parties
                </label>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button
                  onClick={() => {
                    setShowActionModal(false);
                    setSelectedAction('');
                  }}
                  className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const adminNotes = document.getElementById('adminNotes').value;
                    const severity = document.getElementById('severity').value;
                    const sendEmail = document.getElementById('sendEmail').checked;
                    
                    if (!adminNotes.trim()) {
                      setError('Please provide admin notes.');
                      return;
                    }
                    
                    handleAction(selectedAction, adminNotes, severity, sendEmail);
                  }}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? 'Processing...' : 'Confirm Action'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}

export default ReportDetail;